<template>
  <b-container fluid id="school-population" class="mt-4">
    <b-row>
      <b-col>
        <div id="finance-form">
          <div id="alerts" ref="school-population-alert">
            <b-alert
              :show="dismissCountDown"
              dismissible
              variant="success"
              @dismissed="dismissCountDown = 0"
              @dismiss-count-down="countDownChanged"
              >You have successfully added a population record!</b-alert
            >
            <b-alert :show="formError" dismissible variant="warning">
              Something went wrong. Please refresh and try again, or contact the
              site admin.
            </b-alert>
          </div>
          <b-row id="headers">
            <b-col sm="1"></b-col>
            <b-col sm="2">Date</b-col>
            <b-col sm="2">Total Boys</b-col>
            <b-col sm="2">Total Girls</b-col>
            <b-col sm="2">Total Pupils</b-col>
            <b-col sm="2"></b-col>
            <b-col sm="1"></b-col>
          </b-row>
          <b-row>
            <b-col sm="1"></b-col>
            <b-col sm="2">
              <b-form-input
                name="finance_date"
                type="date"
                id="date"
                v-model="dateForm"
                required
              ></b-form-input>
            </b-col>
            <b-col sm="2">
              <b-form-input
                name="currency"
                type="number"
                id="total-boys"
                v-model.number="totalBoysForm"
              ></b-form-input>
            </b-col>
            <b-col sm="2">
              <b-form-input
                :rows="1"
                type="number"
                name="total-girls"
                v-model.number="totalGirlsForm"
              ></b-form-input>
            </b-col>
            <b-col sm="2">
              <b-form-input
                :rows="1"
                type="number"
                name="total-pupils"
                v-model.number="totalPupilsForm"
              ></b-form-input>
            </b-col>
            <b-col sm="2">
              <b-button
                v-if="isAdmin || isSRC"
                :variant="'primary'"
                @click="addOrUpdate"
              >
                {{ buttonTitle }}
              </b-button>
            </b-col>
            <b-col sm="1"></b-col>
          </b-row>
          <b-row>
            <b-col>
              <p class="error" v-show="pupilCountError">
                Total pupils has to equal the sum of total boys and total girls
              </p>
            </b-col>
          </b-row>
          <br />
        </div>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <div id="finance-table">
          <b-table
            v-cloak
            responsive
            small
            :fields="fields"
            :items="populationTable"
            :striped="true"
            :bordered="true"
            :hover="true"
            stacked="md"
            :filter="filter"
            :current-page="currentPage"
            @filtered="onFiltered"
            :per-page="perPage"
            id="financing-table"
          >
            <template slot="cell(id)" slot-scope="row">
              <b-link v-if="isAdmin || isSRC" @click.stop="editSchoolPopulation(row)"
                >Edit</b-link
              >
            </template>
            <template slot="cell(delete)" slot-scope="row">
              <b-link
                class="delete"
                v-if="isAdmin || isSRC"
                @click.stop="deleteSchoolPopulation(row.item.id)"
                >DELETE</b-link
              >
            </template>
          </b-table>
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import SchoolService from "../services/SchoolService";
import { flash } from "../mixins/flashes";
import { scrollTo } from "../mixins/scrollTo";

export default {
  name: "SchoolPopulation",
  mixins: [flash, scrollTo],
  data() {
    return {
      dateForm: null,
      totalBoysForm: 0,
      totalGirlsForm: 0,
      totalPupilsForm: 0,
      id: "",
      disabled: false,
      required: false,
      changeTitle: false,
      fields: [
        { key: "population_date", label: "Date", sortable: true },
        { key: "total_boys", label: "Total Boys", sortable: true },
        { key: "total_girls", label: "Total Girls", sortable: true },
        { key: "total_pupils", label: "Total Pupils", sortable: true },
        { key: "id", label: "Edit" },
        { key: "delete", label: "Delete" },
      ],
      filter: "",
      totalRows: this.rows,
      currentPage: 1,
      perPage: 20,
      formError: false,
    };
  },
  computed: {
    ...mapGetters({
      isAdmin: "auth/isAdmin",
       isSRC: "auth/isSRC",
    }),
    buttonTitle() {
      return this.changeTitle ? "Edit" : "Submit";
    },
    populationTable: {
      get() {
        return this.$store.state.schools.populations;
      },
    },
    rows() {
      return this.obs.length;
    },
    pupilCountError() {
      return this.totalBoysForm + this.totalGirlsForm !== this.totalPupilsForm;
    },
  },
  methods: {
    ...mapActions({
      GET_POPULATIONS: "schools/GET_SCHOOL_POPULATIONS",
    }),
    onFiltered(filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },
    async addSchoolPopulation() {
      if (this.dateForm === null || this.totalPupilsForm === null) {
        this.formError = true;
        this.flashError();
        return;
      }
      await SchoolService.addSchoolPopulation({
        date: this.dateForm,
        school_uuid: this.$route.params.id,
        total_boys: this.totalBoysForm,
        total_girls: this.totalGirlsForm,
        total_pupils: this.totalPupilsForm,
      });
      await this.scrollTo("school-population-alert");
      await this.flashSuccess();
      this.clearForm();
      this.formError = false;
      this.changeTitle = false;
      await this.GET_POPULATIONS();
    },
    async updateSchoolPopulation() {
      if (this.dateForm === null || this.totalPupilsForm === null) {
        this.formError = true;
        this.flashError();
        return;
      }
      await SchoolService.updateSchoolPopulation({
        id: this.id,
        date: this.dateForm,
        school_id: parseInt(this.$route.params.id),
        total_boys: this.totalBoysForm,
        total_girls: this.totalGirlsForm,
        total_pupils: this.totalPupilsForm,
      });
      await this.scrollTo("school-population-alert");
      await this.flashSuccess();
      this.clearForm();
      this.formError = false;
      this.changeTitle = false;
      await this.GET_POPULATIONS();
    },
    async addOrUpdate() {
      if (this.buttonTitle === "Edit") {
        this.updateSchoolPopulation();
      } else {
        this.addSchoolPopulation();
      }
    },
    async deleteSchoolPopulation(params) {
      const confirmDelete = confirm(
        "Are you sure you want to delete this population record?"
      );
      if (confirmDelete) {
        await SchoolService.deleteSchoolPopulation(params);
        await this.GET_POPULATIONS();
      }
    },
    clearForm() {
      this.dateForm = null;
      this.totalBoysForm = null;
      this.totalGirlsForm = null;
      this.totalPupilsForm = null;
      this.govSalariesForm = "";
      this.govGrantForm = "";
      this.otherForm = "";
      this.id = "";
    },
    editSchoolPopulation(row) {
      this.changeTitle = true;
      this.id = row.item.id;
      this.dateForm = row.item.population_date;
      this.totalBoysForm = row.item.total_boys;
      this.totalGirlsForm = row.item.total_girls;
      this.totalPupilsForm = row.item.total_pupils;

      this.id = row.item.id;
      this.populationTable.splice(row.index, 1);
    },
  },
  created() {
    this.GET_POPULATIONS();
  },
};
</script>

<style scoped>
#headers,
#date {
  text-align: left;
}
#edit_icon {
  float: left;
}
#delete_icon {
  width: 20%;
}
h1,
#add_icon {
  display: inline;
}
#add_icon {
  width: 3%;
  opacity: 80%;
}
table th,
table tr {
  text-align: center;
  word-break: break-all;
  word-wrap: break-word;
  overflow-wrap: break-word;
}
table thead {
  background: #bf9660;
}
td {
  width: 100px;
  text-align: center;
}
table tr td {
  padding: 10px;
}
table tr:nth-child(odd) {
  background: #f2f2f2;
}
table tr:nth-child(1) {
  background: #bf9660;
  color: #fff;
}
#headers {
  text-align: center;
}
.col-sm-2,
.col-sm-1 {
  padding: 0 5px 0 5px;
}
</style>
